import {tokens} from '@pleo-io/telescope'
import React, {FC} from 'react'
import styled, {keyframes} from 'styled-components'

import RichText, {hasRichText, Props as RichTextProps} from '@helios/shared/components/rich-text'
import {useSessionStorage} from '@helios/shared/hooks/use-session-storage'
import closeIcon from '@helios/shared/images/icons/close-black.svg'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {colors, zIndex} from '@helios/shared/theme'

const buttonSize = '20px'

const NotificationBannerContainer: FC = () => {
    const globalStrings = useGlobalStrings()
    const [isVisible, setIsVisible] = React.useState(false)
    const [isBannerDismissed, setIsBannerDismissed] = useSessionStorage(
        'has-dismissed-notification-banner'
    )

    const message = globalStrings.notification_banner_message

    // The banner will not be visible by default.
    // The useEffect will then make it visible only if the banner has not been dismissed
    React.useEffect(() => {
        if (!isBannerDismissed) {
            setIsVisible(true)
        }
    }, [isBannerDismissed])

    const dismissNotificationBanner = () => {
        setIsVisible(false)
        setIsBannerDismissed('dismissed')
    }

    if (!hasRichText(message) || !isVisible) {
        return null
    }

    return (
        <NotificationBanner
            message={message}
            closeButtonText={globalStrings.close_button_text}
            dismissNotificationBanner={dismissNotificationBanner}
        />
    )
}

interface NotificationBannerProps {
    message: RichTextProps['content']
    closeButtonText: string
    isPersistent?: boolean
    dismissNotificationBanner: () => void
}

export const NotificationBanner: FC<NotificationBannerProps> = ({
    isPersistent,
    message,
    closeButtonText,
    dismissNotificationBanner,
}) => {
    return (
        <Wrapper>
            <MessageContainer $isPersistent={isPersistent}>
                <RichText size="xs" font="secondary" content={message} bgColor="black" />
            </MessageContainer>

            {!isPersistent && (
                <CloseButtonContainer>
                    <CloseButton
                        aria-label={closeButtonText}
                        onClick={() => dismissNotificationBanner()}
                    >
                        <img alt="" aria-hidden="true" src={closeIcon} />
                    </CloseButton>
                </CloseButtonContainer>
            )}
        </Wrapper>
    )
}

const slideIn = keyframes`
    from {
        transform: translateY(-100%);
    }
    to {
        transform: translateY(0);
    }
`

const Wrapper = styled.div`
    z-index: ${zIndex.notificationBanner};
    display: flex;
    padding: ${tokens.spacing12};
    background: ${colors.banner.background};
    justify-content: flex-end;
    animation: ${slideIn} ${tokens.smooth};
`

interface MessageContainerProps {
    $isPersistent?: boolean
}

const MessageContainer = styled.div<MessageContainerProps>`
    width: 100%;
    padding-left: ${(props) => (props.$isPersistent ? 0 : buttonSize)};
    text-align: center;
`

const CloseButtonContainer = styled.div`
    display: flex;
    align-items: center;
`

const CloseButton = styled.button`
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;

    img {
        width: ${buttonSize};
        height: ${buttonSize};
    }
`

export default NotificationBannerContainer
